import { useState, useEffect, FormEvent, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitCheckout, PaymentMethod } from '@/lib/api';
import { useCart } from '@/contexts/CartContext';
import { calculateShipping, getShippingMethodLabel, hasToteBag } from '@/utils/shipping';
import { BANK_TRANSFER_INFO } from '@/lib/bankInfo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

declare global {
  interface Window {
    UnivapayCheckout?: {
      create: (options: Record<string, unknown>) => { open: () => void };
    };
  }
}

// UnivaPayの管理画面で発行される「App Token」(公開情報。フロントに直書きしてよい)
const UNIVAPAY_APP_ID = import.meta.env.VITE_UNIVAPAY_APP_ID as string | undefined;
const CARD_PAYMENT_READY = Boolean(UNIVAPAY_APP_ID);

type FormData = {
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  zip: string;
  prefecture: string;
  city: string;
  address: string;
  apt: string;
  notes: string;
};

const PREFECTURES = ['北海道','青森県','岩手県','宮城県','秋田県','山形県','福島県','茨城県','栃木県','群馬県','埼玉県','千葉県','東京都','神奈川県','新潟県','富山県','石川県','福井県','山梨県','長野県','岐阜県','静岡県','愛知県','三重県','滋賀県','京都府','大阪府','兵庫県','奈良県','和歌山県','鳥取県','島根県','岡山県','広島県','山口県','徳島県','香川県','愛媛県','高知県','福岡県','佐賀県','長崎県','熊本県','大分県','宮崎県','鹿児島県','沖縄県'];

export default function CheckoutPage() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');
  const [form, setForm] = useState<FormData>({
    lastName: '', firstName: '', email: '', phone: '',
    zip: '', prefecture: '東京都', city: '', address: '', apt: '', notes: '',
  });
  const [cardTokenId, setCardTokenId] = useState('');
  const [cardError, setCardError] = useState('');
  const [cardWidgetOpen, setCardWidgetOpen] = useState(false);

  useEffect(() => {
    const handleTokenCreated = (e: Event) => {
      const detail = (e as CustomEvent).detail as { id?: string; data?: { id?: string } } | undefined;
      const id = detail?.id ?? detail?.data?.id;
      if (id) {
        setCardTokenId(id);
        setCardError('');
      } else {
        setCardError('カード情報の登録に失敗しました。もう一度お試しください。');
      }
      setCardWidgetOpen(false);
    };
    const handleClosed = () => setCardWidgetOpen(false);
    const handleError = () => {
      setCardError('カード情報の登録に失敗しました。もう一度お試しください。');
      setCardWidgetOpen(false);
    };

    window.addEventListener('univapay:token-created', handleTokenCreated);
    window.addEventListener('univapay:closed', handleClosed);
    window.addEventListener('univapay:error', handleError);
    return () => {
      window.removeEventListener('univapay:token-created', handleTokenCreated);
      window.removeEventListener('univapay:closed', handleClosed);
      window.removeEventListener('univapay:error', handleError);
    };
  }, []);

  const handleOpenCardWidget = () => {
    if (!window.UnivapayCheckout || !UNIVAPAY_APP_ID) return;
    setCardError('');
    setCardWidgetOpen(true);
    const checkout = window.UnivapayCheckout.create({
      appId: UNIVAPAY_APP_ID,
      checkout: 'token',
      tokenType: 'one_time',
      email: form.email || undefined,
    });
    checkout.open();
  };

  const slugs = useMemo(() => items.map(i => i.product.slug), [items]);
  const methodLabel = getShippingMethodLabel(slugs);
  const shipping = calculateShipping(slugs, form.prefecture);
  const total = subtotal + shipping;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (paymentMethod === 'card' && (!CARD_PAYMENT_READY || !cardTokenId)) return;
    setLoading(true);
    setError('');

    try {
      const result = await submitCheckout({
        items: items.map(i => ({ slug: i.product.slug, quantity: i.quantity, size: i.selectedSize })),
        paymentMethod,
        transactionTokenId: paymentMethod === 'card' ? cardTokenId : undefined,
        recipient: {
          lastName: form.lastName,
          firstName: form.firstName,
          email: form.email,
          phone: form.phone,
          zip: form.zip,
          prefecture: form.prefecture,
          city: form.city,
          address: form.address,
          apt: form.apt || undefined,
        },
        notes: form.notes || undefined,
      });

      if (result.error) throw new Error(result.error);

      clearCart();
      navigate(`/order-complete?orderId=${result.orderNumber}`, {
        state: {
          email: form.email,
          shippingFee: result.shippingFee ?? shipping,
          methodLabel,
          paymentMethod: result.paymentMethod ?? paymentMethod,
          total: result.total ?? total,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : '注文処理中にエラーが発生しました');
      if (paymentMethod === 'card') {
        // one_timeトークンは一度使うと失効するため、失敗時は再度カード情報の入力からやり直してもらう
        setCardTokenId('');
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = items.length > 0 && (paymentMethod === 'bank_transfer' || (CARD_PAYMENT_READY && !!cardTokenId));

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="bg-secondary-50 py-16 px-6 text-center">
          <p className="text-xs tracking-[0.3em] text-primary-500 mb-3 uppercase"
            style={{ fontFamily: "var(--font-heading)" }}>
            Checkout
          </p>
          <h1 className="text-4xl md:text-6xl font-light text-foreground-950"
            style={{ fontFamily: "var(--font-heading)" }}>
            Checkout
          </h1>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-14">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
              <p className="text-foreground-500 text-base" style={{ fontFamily: "var(--font-body)" }}>
                カートに商品がありません
              </p>
              <Link to="/products" className="text-sm text-foreground-400 hover:text-primary-500 tracking-[0.15em] underline cursor-pointer" style={{ fontFamily: "var(--font-heading)" }}>
                Shop Now
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-10">
                <div>
                  <h2 className="text-xl font-light text-foreground-950 mb-6"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    Shipping Info
                  </h2>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                        style={{ fontFamily: "var(--font-heading)" }}>
                        姓 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={form.lastName}
                        onChange={handleChange}
                        className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                        style={{ fontFamily: "var(--font-body)" }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                        style={{ fontFamily: "var(--font-heading)" }}>
                        名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={form.firstName}
                        onChange={handleChange}
                        className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                        style={{ fontFamily: "var(--font-body)" }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                        style={{ fontFamily: "var(--font-heading)" }}>
                        メールアドレス <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                        style={{ fontFamily: "var(--font-body)" }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                        style={{ fontFamily: "var(--font-heading)" }}>
                        電話番号 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                        style={{ fontFamily: "var(--font-body)" }}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                      style={{ fontFamily: "var(--font-heading)" }}>
                      郵便番号 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="zip"
                      required
                      placeholder="000-0000"
                      value={form.zip}
                      onChange={handleChange}
                      className="w-full sm:w-52 border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                      style={{ fontFamily: "var(--font-heading)" }}>
                      都道府県 <span className="text-primary-500">*</span>
                    </label>
                    <select
                      name="prefecture"
                      required
                      value={form.prefecture}
                      onChange={handleChange}
                      className="w-full sm:w-52 border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer rounded-lg"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {PREFECTURES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {hasToteBag(slugs) && (
                      <p className="text-xs text-foreground-500 mt-2" style={{ fontFamily: "var(--font-body)" }}>
                        <i className="ri-information-line mr-1"></i>
                        トートバックを含むご注文のため、ヤマト運輸での発送となります。地域により送料が変動します。
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                      style={{ fontFamily: "var(--font-heading)" }}>
                      市区町村 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={form.city}
                      onChange={handleChange}
                      className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                      style={{ fontFamily: "var(--font-heading)" }}>
                      番地・建物名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      value={form.address}
                      onChange={handleChange}
                      className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs text-foreground-700 mb-2 tracking-wider"
                      style={{ fontFamily: "var(--font-heading)" }}>
                      部屋番号など（任意）
                    </label>
                    <input
                      type="text"
                      name="apt"
                      value={form.apt}
                      onChange={handleChange}
                      className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors rounded-lg"
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-light text-foreground-950 mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    Notes
                  </h2>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    placeholder="配送に関するご要望など（任意）"
                    className="w-full border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none rounded-lg"
                    style={{ fontFamily: "var(--font-body)" }}
                  />
                </div>

                <div>
                  <h2 className="text-xl font-light text-foreground-950 mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    Payment
                  </h2>

                  <div className="space-y-3">
                    <label className={`flex items-center gap-4 border rounded-xl p-5 cursor-pointer ${paymentMethod === 'bank_transfer' ? 'border-primary-400 bg-background-50' : 'border-background-300 bg-background-50'}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        checked={paymentMethod === 'bank_transfer'}
                        onChange={() => setPaymentMethod('bank_transfer')}
                        className="w-4 h-4 accent-primary-500"
                      />
                      <div>
                        <p className="text-sm text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>銀行振込</p>
                        <p className="text-xs text-foreground-500" style={{ fontFamily: "var(--font-body)" }}>ご注文確定後、振込先をご案内します。入金確認後7日以内に発送いたします。振込手数料はお客様のご負担となります</p>
                      </div>
                    </label>

                    {paymentMethod === 'bank_transfer' && (
                      <div className="ml-8 border border-background-200 rounded-lg p-4 bg-white text-sm text-foreground-700 space-y-1">
                        <p>{BANK_TRANSFER_INFO.bankName} {BANK_TRANSFER_INFO.branchName}</p>
                        <p>{BANK_TRANSFER_INFO.accountType} {BANK_TRANSFER_INFO.accountNumber}</p>
                        <p>{BANK_TRANSFER_INFO.accountHolder}</p>
                        <p className="text-xs text-foreground-500 pt-1">※振込手数料はお客様のご負担となります</p>
                      </div>
                    )}

                    <label className={`flex items-center gap-4 border rounded-xl p-5 ${CARD_PAYMENT_READY ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'} ${paymentMethod === 'card' ? 'border-primary-400 bg-background-50' : 'border-background-300 bg-background-50'}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        checked={paymentMethod === 'card'}
                        disabled={!CARD_PAYMENT_READY}
                        onChange={() => setPaymentMethod('card')}
                        className="w-4 h-4 accent-primary-500"
                      />
                      <div>
                        <p className="text-sm text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>クレジットカード</p>
                        <p className="text-xs text-foreground-500" style={{ fontFamily: "var(--font-body)" }}>
                          {CARD_PAYMENT_READY ? '安全な決済でその場でお支払いいただけます' : '準備中です。もうしばらくお待ちください'}
                        </p>
                      </div>
                    </label>

                    {paymentMethod === 'card' && CARD_PAYMENT_READY && (
                      <div className="ml-8 border border-background-200 rounded-lg p-4 bg-white space-y-3">
                        <button
                          type="button"
                          onClick={handleOpenCardWidget}
                          disabled={cardWidgetOpen}
                          className="text-sm bg-primary-500 text-white px-6 py-2.5 rounded-full disabled:opacity-50 cursor-pointer"
                          style={{ fontFamily: "var(--font-heading)" }}
                        >
                          {cardWidgetOpen ? '入力中...' : cardTokenId ? 'カード情報を登録し直す' : 'カード情報を入力する'}
                        </button>
                        {cardTokenId && (
                          <p className="text-xs text-accent-600 flex items-center gap-1">
                            <i className="ri-check-line"></i> カード情報を登録しました
                          </p>
                        )}
                        {cardError && (
                          <p className="text-xs text-primary-600">{cardError}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-secondary-50 rounded-xl p-6 sticky top-24">
                  <h2 className="text-xl font-light text-foreground-950 mb-6"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    Order Summary
                  </h2>
                  <div className="space-y-4 mb-6">
                    {items.map(item => (
                      <div key={item.product.slug + (item.selectedSize ?? '')} className="flex gap-3">
                        <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={item.product.imageUrl ?? ''}
                            alt={item.product.name}
                            className="w-full h-full object-contain object-center"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground-800 leading-snug mb-1 truncate"
                            style={{ fontFamily: "var(--font-heading)" }}>
                            {item.product.name}{item.selectedSize ? `(${item.selectedSize})` : ''}
                          </p>
                          <p className="text-xs text-foreground-600 mt-1" style={{ fontFamily: "var(--font-body)" }}>
                            ¥{item.product.price.toLocaleString()} × {item.quantity}
                          </p>
                        </div>
                        <p className="text-xs text-foreground-950 flex-shrink-0"
                          style={{ fontFamily: "var(--font-heading)" }}>
                          ¥{(item.product.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-background-300 pt-4 space-y-2 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600" style={{ fontFamily: "var(--font-body)" }}>小計</span>
                      <span className="text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>¥{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600" style={{ fontFamily: "var(--font-body)" }}>配送方法</span>
                      <span className="text-foreground-900 text-xs" style={{ fontFamily: "var(--font-heading)" }}>{methodLabel}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600" style={{ fontFamily: "var(--font-body)" }}>送料</span>
                      <span className="text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
                        ¥{shipping.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-background-300">
                      <span className="text-base text-foreground-950" style={{ fontFamily: "var(--font-body)" }}>合計</span>
                      <span className="text-xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>¥{total.toLocaleString()}</span>
                    </div>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-primary-100 border border-primary-200 rounded-lg">
                      <p className="text-xs text-primary-800" style={{ fontFamily: "var(--font-body)" }}>{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !canSubmit}
                    className="w-full bg-primary-500 text-white py-4 text-sm tracking-[0.15em] hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed rounded-full"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-lock-line"></i> Place Order
                      </span>
                    )}
                  </button>
                  <p className="text-[10px] text-foreground-500 text-center mt-3" style={{ fontFamily: "var(--font-body)" }}>
                    <i className="ri-shield-check-line mr-1"></i>
                    安全な暗号化通信で保護されています
                  </p>
                </div>
              </div>
            </div>
          </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
